import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { MAX_CHAT_PINS, pinKey } from '../../lib/chat/envelope';
// Finance and contracts wait for PinAuthorizer. Without Cerbos they
// would only stamp a desk name into the prompt.
export const DEFAULT_PIN_OPTIONS = [
    {
        label: 'RFI Response workspace',
        pin: {
            surface: 'rfi_response'
        }
    }
];
/**
 * W0-E — personal unshared /assistant pin chips. Cap 8. Desk-bound
 * callers must not mount this.
 */ export function ChatPins({ pins, onChange, options, onOpen, notice, loading }) {
    const [open, setOpen] = useState(false);
    const taken = useMemo(()=>new Set(pins.map(pinKey)), [
        pins
    ]);
    const available = options.filter((o)=>!taken.has(pinKey(o.pin)));
    const atCap = pins.length >= MAX_CHAT_PINS;
    const add = (pin)=>{
        if (atCap) return;
        onChange([
            ...pins,
            pin
        ]);
        setOpen(false);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex shrink-0 flex-wrap items-center gap-1.5",
        "data-testid": Testids.chatPins.root,
        children: [
            pins.map((pin)=>/*#__PURE__*/ _jsxs("span", {
                    "data-testid": Testids.chatPins.chip,
                    className: "inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] text-foreground",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "truncate",
                            children: pin.label || pin.surface
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            "aria-label": `Remove ${pin.label || pin.surface}`,
                            "data-testid": Testids.chatPins.remove,
                            className: "text-muted-foreground hover:text-foreground",
                            onClick: ()=>onChange(pins.filter((p)=>pinKey(p) !== pinKey(pin))),
                            children: "×"
                        })
                    ]
                }, pinKey(pin))),
            /*#__PURE__*/ _jsxs("div", {
                className: "relative",
                children: [
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        "data-testid": Testids.chatPins.add,
                        disabled: atCap,
                        onClick: ()=>{
                            if (!open) onOpen?.();
                            setOpen(!open);
                        },
                        className: "whitespace-nowrap rounded-full border border-dashed border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:border-muted-foreground disabled:opacity-50",
                        children: "Add context"
                    }),
                    open ? /*#__PURE__*/ _jsxs("div", {
                        "data-testid": Testids.chatPins.picker,
                        className: "absolute left-0 z-20 mt-1 max-h-56 min-w-[220px] overflow-y-auto rounded-md border border-border bg-card p-1 shadow-md",
                        children: [
                            notice ? /*#__PURE__*/ _jsx("div", {
                                className: "px-2 py-1.5 text-[length:var(--fs-ui-eyebrow)] text-muted-foreground",
                                children: notice
                            }) : null,
                            loading ? /*#__PURE__*/ _jsx("div", {
                                "data-testid": Testids.chatPins.loading,
                                "aria-live": "polite",
                                className: "px-2 py-1.5 text-[length:var(--fs-ui-eyebrow)] text-muted-foreground",
                                children: "Loading pursuits…"
                            }) : null,
                            available.length === 0 ? loading ? null : /*#__PURE__*/ _jsx("div", {
                                className: "px-2 py-1.5 text-[11px] text-muted-foreground",
                                children: "Nothing to add"
                            }) : available.map((opt)=>/*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    "data-testid": Testids.chatPins.option,
                                    "data-surface": opt.pin.surface,
                                    className: "block w-full truncate rounded px-2 py-1 text-left text-[12px] hover:bg-muted",
                                    onClick: ()=>add({
                                            ...opt.pin,
                                            label: opt.label
                                        }),
                                    children: opt.label
                                }, pinKey(opt.pin)))
                        ]
                    }) : null
                ]
            })
        ]
    });
}
